import React, { useEffect, useState } from 'react';
import { getQaScore } from '../../../../Api/apiUtils';
import { Info } from 'react-feather';
import { Tooltip } from 'reactstrap';
import { Button, DescriptionList, EmptyState, Loader, Table } from '../../../../ui';
import { StatTile } from './ui-pending';
import SideDrawer from './ui-pending/SideDrawer';
import './SeeQAScore.scss';
import { stripRichText } from './Calendar/richTextUtils';

interface QAScoreProps {
    teacherUserId: string;
    teacherName?: string;
}

interface FeedbackItem {
    classId?: string;
    feedback?: any;
}

const SeeQAScore: React.FC<QAScoreProps> = ({ teacherUserId, teacherName }) => {
    const [qaData, setQaData] = useState<FeedbackItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFeedback, setSelectedFeedback] = useState<{ classId: string; feedback: any } | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        if (teacherUserId) {
            fetchQAScoreData();
        }
    }, [teacherUserId]);

    const fetchQAScoreData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getQaScore(teacherUserId);
            
            if (data) {
                setQaData(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Error fetching QA Score data:', err);
            setError('No Data Found.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            const day = date.getDate();
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        } catch (e) {
            return dateString;
        }
    };

    const calculateScorePercentage = (feedback: any): number | string => {
        if (feedback?.totalScorePercentage !== undefined) {
            return Math.round(feedback.totalScorePercentage);
        }
        
        // Calculate from individual scores if totalScorePercentage is not available
        const scoreKeys = Object.keys(feedback || {}).filter(key => 
            typeof feedback[key] === 'object' && 
            feedback[key] !== null && 
            feedback[key].score !== undefined
        );
        
        if (scoreKeys.length === 0) return 'N/A';
        
        const totalScore = scoreKeys.reduce((sum, key) => {
            const scoreObj = feedback[key];
            return sum + (scoreObj.score || 0);
        }, 0);
        
        const maxScore = scoreKeys.length * 10; // Assuming max score is 10 per criterion
        const percentage = (totalScore / maxScore) * 100;
        return Math.round(percentage);
    };

    const handleViewDetails = (classId: string, feedback: any) => {
        setSelectedFeedback({ classId, feedback });
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setSelectedFeedback(null);
    };

    const calculateTotalScore = (feedback: any): number | null => {
        if (feedback?.totalscore !== undefined && feedback?.totalscore !== null) {
            return feedback.totalscore;
        }
        if (feedback?.outOf !== undefined && feedback?.totalScorePercentage !== undefined) {
            return Math.round((feedback.totalScorePercentage / 100) * feedback.outOf);
        }
        
        const scoreKeys = Object.keys(feedback || {}).filter(key => 
            typeof feedback[key] === 'object' && 
            feedback[key] !== null && 
            feedback[key].score !== undefined &&
            !feedback[key].na
        );
        
        if (scoreKeys.length === 0) return null;
        
        const totalScore = scoreKeys.reduce((sum, key) => {
            const scoreObj = feedback[key];
            return sum + (scoreObj.score || 0);
        }, 0);
        
        return totalScore;
    };

    const calculateOutOf = (feedback: any): number => {
        if (feedback?.outOf !== undefined && feedback?.outOf !== null) {
            return feedback.outOf;
        }
        
        const scoreKeys = Object.keys(feedback || {}).filter(key => 
            typeof feedback[key] === 'object' && 
            feedback[key] !== null && 
            feedback[key].score !== undefined &&
            !feedback[key].na
        );
        
        return scoreKeys.length * 10; // Assuming max score is 10 per criterion
    };

    const getScoreItems = (feedback: any) => {
        const scoreItems: any[] = [];
        const excludedKeys = ['Auditor', 'Student', 'Course', 'Date of Audit', 'Date of Class', 
                             'Class Number', 'Lesson Number', 'Teacher', 'Age', 'Level', 
                             'Timezone', 'Graded Certification', 'Audit Category', 'Positives', 
                             'Areas of Improvement', 'Overall Feedback', 'totalscore', 'outOf', 
                             'totalScorePercentage'];
        
        Object.keys(feedback || {}).forEach(key => {
            if (excludedKeys.includes(key)) return;
            
            const value = feedback[key];
            if (typeof value === 'object' && value !== null && value.score !== undefined) {
                scoreItems.push({
                    title: key,
                    score: value.score,
                    maxScore: 10,
                    description: value.rating_descriptions?.[Math.floor(value.score)] || '',
                    ratingDescriptions: value.rating_descriptions || {},
                    na: value.na || false
                });
            }
        });
        
        return scoreItems;
    };

    const toggleTooltip = (id: string) => {
        setTooltipOpen(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    if (loading) {
        return <Loader label="Loading QA scores…" />;
    }

    if (error) {
        return <EmptyState title={error} description="No academic QA audits could be loaded for this teacher." />;
    }

    return (
        <div className="tp-qascore">
            <div className="tp-qascore__header">
                <h2 className="ds-card__title">Academic QA Score</h2>
                <p className="ds-card__subtitle">
                    This list includes the score of all the classes that were audited.
                </p>
            </div>

            {qaData.length === 0 ? (
                <EmptyState title="No audit data available at the moment." />
            ) : (
                <Table caption="Academic QA audits">
                    <thead>
                        <tr>
                            <th>Auditor Name</th>
                            <th>Student</th>
                            <th>Class No</th>
                            <th>Course</th>
                            <th>Audited On</th>
                            <th className="ds-num">Audit Score %</th>
                            <th>Audit Category</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {qaData.map((item, index) => {
                            const feedback = item.feedback || {};
                            const scorePercentage = calculateScorePercentage(feedback);
                            const isSelected = selectedFeedback?.classId === item.classId;

                            return (
                                <tr
                                    key={item.classId || index}
                                    className={`tp-qascore__row ${isSelected ? 'tp-qascore__row--selected' : ''}`}
                                    onClick={() => handleViewDetails(item.classId || '', feedback)}
                                >
                                    <td>{feedback.Auditor || 'N/A'}</td>
                                    <td>{feedback.Student || 'N/A'}</td>
                                    <td>Class {feedback['Class Number'] || 'N/A'}</td>
                                    <td>{feedback.Course || 'N/A'}</td>
                                    <td>{formatDate(feedback['Date of Audit'])}</td>
                                    <td className="ds-num">{scorePercentage !== 'N/A' ? `${scorePercentage}%` : 'N/A'}</td>
                                    <td>{feedback['Audit Category'] || 'N/A'}</td>
                                    <td>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewDetails(item.classId || '', feedback);
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            {/* Details panel - opens on top */}
            {drawerOpen && selectedFeedback && (
                <QAScoreDetails
                    feedback={selectedFeedback.feedback}
                    onClose={handleCloseDrawer}
                    formatDate={formatDate}
                    calculateScorePercentage={calculateScorePercentage}
                    calculateTotalScore={calculateTotalScore}
                    calculateOutOf={calculateOutOf}
                    getScoreItems={getScoreItems}
                    tooltipOpen={tooltipOpen}
                    toggleTooltip={toggleTooltip}
                />
            )}
        </div>
    );
};

interface QAScoreDetailsProps {
    feedback: any;
    onClose: () => void;
    formatDate: (dateString: string | undefined) => string;
    calculateScorePercentage: (feedback: any) => number | string;
    calculateTotalScore: (feedback: any) => number | null;
    calculateOutOf: (feedback: any) => number;
    getScoreItems: (feedback: any) => any[];
    tooltipOpen: { [key: string]: boolean };
    toggleTooltip: (id: string) => void;
}

const renderRichTextFeedback = (html: string, expanded: boolean, showReadMore: boolean) => {
    const plainText = stripRichText(html);
    const looksLikeHtml = html.includes("<") && html.includes(">");

    if (!showReadMore || expanded) {
        if (looksLikeHtml) {
            return <span className="tp-qascore-detail__rich" dangerouslySetInnerHTML={{ __html: html }} />;
        }
        return <span>{plainText}</span>;
    }

    return <span>{`${plainText.substring(0, 150)}...`}</span>;
};

const QAScoreDetails: React.FC<QAScoreDetailsProps> = ({ 
    feedback, 
    onClose, 
    formatDate, 
    calculateScorePercentage, 
    calculateTotalScore, 
    calculateOutOf, 
    getScoreItems,
    tooltipOpen,
    toggleTooltip
}) => {
    const [expandedPositive, setExpandedPositive] = useState(false);
    const [expandedImprovements, setExpandedImprovements] = useState(false);
    
    const scorePercentage = calculateScorePercentage(feedback);
    const totalScore = calculateTotalScore(feedback);
    const outOf = calculateOutOf(feedback);
    const scoreItems = getScoreItems(feedback);
    
    const positiveText = feedback?.Positives || '';
    const improvementsText = feedback?.['Areas of Improvement'] || '';
    const showReadMorePositive = stripRichText(positiveText).length > 150;
    const showReadMoreImprovements = stripRichText(improvementsText).length > 150;

    const handleTooltipMouseEnter = (id: string) => {
        toggleTooltip(id);
    };

    const handleTooltipMouseLeave = (id: string) => {
        toggleTooltip(id);
    };

    return (
        <SideDrawer
            label="Academic QA audit details"
            onClose={onClose}
            header={
                <DescriptionList
                    items={[
                        { label: 'Auditor Name', value: feedback?.Auditor || 'N/A' },
                        { label: 'Student Name', value: feedback?.Student || 'N/A' },
                        { label: 'Class Number', value: feedback?.['Class Number'] || 'N/A' },
                        { label: 'Course Name', value: feedback?.Course || 'N/A' },
                        { label: 'Audit Date', value: formatDate(feedback?.['Date of Audit']) },
                    ]}
                />
            }
        >
            <div className="tp-qascore-detail">
                <div className="tp-qascore-detail__scores">
                    <StatTile
                        tone="primary"
                        label="Audit Score %"
                        value={scorePercentage !== 'N/A' ? `${scorePercentage}%` : 'N/A'}
                    />
                    {totalScore !== null && outOf && (
                        <StatTile tone="primary" label="Academic QA Score" value={`${totalScore}/${outOf}`} />
                    )}
                </div>

                {scoreItems.map((item, index) => {
                    const tooltipId = `tooltip-${index}`;
                    const hasDescriptions = item.ratingDescriptions && Object.keys(item.ratingDescriptions).length > 0;

                    return (
                        <section key={index} className="tp-qascore-detail__section">
                            <h3 className="tp-qascore-detail__section-title">{item.title}</h3>
                            <div className="tp-qascore-detail__item">
                                {hasDescriptions && (
                                    <>
                                        <span
                                            id={tooltipId}
                                            className="tp-qascore-detail__info"
                                            onMouseEnter={() => handleTooltipMouseEnter(tooltipId)}
                                            onMouseLeave={() => handleTooltipMouseLeave(tooltipId)}
                                        >
                                            <Info strokeWidth={2.5} size={16} aria-hidden />
                                        </span>
                                        <Tooltip
                                            placement="left"
                                            isOpen={tooltipOpen[tooltipId] || false}
                                            target={tooltipId}
                                            toggle={() => toggleTooltip(tooltipId)}
                                            trigger="hover focus"
                                            popperClassName="tp-qascore-tooltip"
                                            innerClassName="tp-qascore-tooltip__inner"
                                            delay={{ show: 200, hide: 100 }}
                                            autohide={false}
                                        >
                                            <div className="tp-qascore-rubric">
                                                <h6 className="tp-qascore-rubric__title">{item.title}</h6>
                                                <div>
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => {
                                                        const description = item.ratingDescriptions[rating.toString()];
                                                        if (!description) return null;
                                                        return (
                                                            <div key={rating} className="tp-qascore-rubric__item">
                                                                <span className="tp-qascore-rubric__rating">{rating}</span>
                                                                <span className="tp-qascore-rubric__text">{description}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </>
                                )}
                                <div className="tp-qascore-detail__item-body">
                                    <div className="tp-qascore-detail__item-text">{item.description || item.title}</div>
                                </div>
                                <div className="tp-qascore-detail__item-score">{item.score}/{item.maxScore}</div>
                            </div>
                        </section>
                    );
                })}

                {positiveText && (
                    <section className="tp-qascore-detail__section">
                        <h3 className="tp-qascore-detail__section-title">Positive</h3>
                        <div className="tp-qascore-detail__text">
                            {renderRichTextFeedback(positiveText, expandedPositive, showReadMorePositive)}
                            {showReadMorePositive && (
                                <Button variant="ghost" size="sm" onClick={() => setExpandedPositive(!expandedPositive)}>
                                    {expandedPositive ? 'Read Less' : 'Read More'}
                                </Button>
                            )}
                        </div>
                    </section>
                )}

                {improvementsText && (
                    <section className="tp-qascore-detail__section">
                        <h3 className="tp-qascore-detail__section-title">Areas of Improvements</h3>
                        <div className="tp-qascore-detail__text">
                            {renderRichTextFeedback(improvementsText, expandedImprovements, showReadMoreImprovements)}
                            {showReadMoreImprovements && (
                                <Button variant="ghost" size="sm" onClick={() => setExpandedImprovements(!expandedImprovements)}>
                                    {expandedImprovements ? 'Read Less' : 'Read More'}
                                </Button>
                            )}
                        </div>
                    </section>
                )}
            </div>
        </SideDrawer>
    );
};

export default SeeQAScore;
